import React from 'react';
import { Helmet } from 'react-helmet-async';
import { FormattedMessage, useIntl } from 'react-intl';
import styled, { keyframes } from 'styled-components';

import { Link } from '../StyledComponents';
import { useSsrContext } from '../ssrContext';

const Page = styled.section`
  min-height: 70vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4rem 1.25rem;
`;

const Card = styled.div`
  position: relative;
  width: 100%;
  max-width: 640px;
  background: ${({ theme }) => theme.colors.cardBg};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 16px;
  box-shadow: ${({ theme }) => theme.shadows.elevated};
  padding: 3rem 2.5rem 2.5rem;
  text-align: center;
  overflow: hidden;

  @media (max-width: 576px) {
    padding: 2.5rem 1.5rem 2rem;
    border-radius: 12px;
  }

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    pointer-events: none;
    background-image:
      radial-gradient(${({ theme }) => theme.colors.borderLight} 1px, transparent 1px);
    background-size: 6px 6px;
    opacity: 0.45;
    mask-image: radial-gradient(circle at 50% 0%, black 0%, transparent 65%);
    -webkit-mask-image: radial-gradient(circle at 50% 0%, black 0%, transparent 65%);
  }
`;

const Eyebrow = styled.div`
  font-family: 'Times New Roman', Georgia, serif;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.32em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.sectionAccent};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  padding: 0.4rem 0;
  margin: 0 auto 1.75rem;
  max-width: 320px;
`;

const stampWobble = keyframes`
  0%   { transform: rotate(-4deg) scale(1); }
  50%  { transform: rotate(-2deg) scale(1.02); }
  100% { transform: rotate(-4deg) scale(1); }
`;

const Code = styled.div`
  font-family: 'Times New Roman', Georgia, serif;
  font-weight: 900;
  font-size: clamp(5.5rem, 18vw, 9rem);
  line-height: 0.95;
  letter-spacing: -0.04em;
  color: ${({ theme }) => theme.colors.text};
  margin: 0.25rem 0 0.5rem;
  position: relative;
  display: inline-block;

  &::after {
    content: 'NOT FOUND';
    position: absolute;
    top: 14%;
    right: -28%;
    transform: rotate(-12deg);
    font-family: 'Times New Roman', Georgia, serif;
    font-size: 0.95rem;
    font-weight: 700;
    letter-spacing: 0.18em;
    color: ${({ theme }) => theme.colors.error};
    border: 2px solid ${({ theme }) => theme.colors.error};
    border-radius: 4px;
    padding: 0.2rem 0.5rem;
    opacity: 0.85;
    animation: ${stampWobble} 4.5s ease-in-out infinite;
    pointer-events: none;

    @media (max-width: 576px) {
      right: -10%;
      font-size: 0.75rem;
    }
  }
`;

const Headline = styled.h1`
  font-family: 'Times New Roman', Georgia, serif;
  font-size: clamp(1.5rem, 4vw, 2rem);
  font-weight: 700;
  line-height: 1.25;
  margin: 0.75rem 0 0.5rem;
  color: ${({ theme }) => theme.colors.text};
`;

const Subheadline = styled.p`
  font-size: 1rem;
  line-height: 1.55;
  color: ${({ theme }) => theme.colors.textSecondary};
  max-width: 460px;
  margin: 0 auto 2rem;
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  justify-content: center;
  position: relative;
  z-index: 1;
`;

const PrimaryAction = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.7rem 1.25rem;
  background: ${({ theme }) => theme.colors.sectionAccent};
  color: ${({ theme }) => theme.colors.textInverse};
  border-radius: 999px;
  font-weight: 600;
  font-size: 0.95rem;
  text-decoration: none;
  transition: filter 0.2s, transform 0.2s;

  &:hover {
    filter: brightness(1.08);
    color: ${({ theme }) => theme.colors.textInverse};
    transform: translateY(-1px);
    text-decoration: none;
  }
`;

const SecondaryAction = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.7rem 1.25rem;
  background: transparent;
  color: ${({ theme }) => theme.colors.text};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 999px;
  font-weight: 600;
  font-size: 0.95rem;
  text-decoration: none;
  transition: background 0.2s, border-color 0.2s, transform 0.2s;

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceHover};
    border-color: ${({ theme }) => theme.colors.sectionAccent};
    color: ${({ theme }) => theme.colors.text};
    transform: translateY(-1px);
    text-decoration: none;
  }
`;

const Tip = styled.p`
  margin-top: 2rem;
  padding-top: 1.25rem;
  border-top: 1px dashed ${({ theme }) => theme.colors.border};
  font-size: 0.85rem;
  color: ${({ theme }) => theme.colors.textMuted};
  font-style: italic;
`;

const NotFoundPage: React.FC = () => {
  const intl = useIntl();
  const { context: ssrContext } = useSsrContext();

  if (ssrContext) {
    (ssrContext as { status?: number; notFound?: boolean }).status = 404;
  }

  const title = intl.formatMessage({
    id: 'notFound.metaTitle',
    defaultMessage: '404 — Bu sayfa arşivimizde yok',
  });
  const description = intl.formatMessage({
    id: 'notFound.metaDescription',
    defaultMessage: 'Aradığınız sayfa arşivde bulunamadı. Gazete arşivinde arama yapabilir veya ana sayfaya dönebilirsiniz.',
  });

  return (
    <Page role="main" aria-labelledby="notfound-title">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="robots" content="noindex,follow" />
      </Helmet>
      <Card>
        <Eyebrow>
          <FormattedMessage id="notFound.eyebrow" defaultMessage="Son baskı · Hata bülteni" />
        </Eyebrow>
        <Code aria-hidden="true">404</Code>
        <Headline id="notfound-title">
          <FormattedMessage
            id="notFound.headline"
            defaultMessage="Bu sayfa arşivimizde yok"
          />
        </Headline>
        <Subheadline>
          <FormattedMessage
            id="notFound.subheadline"
            defaultMessage="Aradığınız haber kupürü ya başka bir baskıya gitmiş ya da hiç basılmamış olabilir. Endişelenmeyin — milyonlarca sayfa hâlâ keşfetmenizi bekliyor."
          />
        </Subheadline>
        <Actions>
          <PrimaryAction to="/">
            <FormattedMessage id="notFound.cta.home" defaultMessage="Ana sayfaya dön" />
          </PrimaryAction>
          <SecondaryAction to="/ara">
            <FormattedMessage id="notFound.cta.search" defaultMessage="Arşivde ara" />
          </SecondaryAction>
          <SecondaryAction to="/tarihte-bugun">
            <FormattedMessage id="notFound.cta.todayInHistory" defaultMessage="Tarihte bugün" />
          </SecondaryAction>
        </Actions>
        <Tip>
          <FormattedMessage
            id="notFound.tip"
            defaultMessage="İpucu: URL'deki tarih veya gazete adını kontrol edin — yazım hataları en sık karşılaşılan suçlulardan."
          />
        </Tip>
      </Card>
    </Page>
  );
};

export default NotFoundPage;
